// Imports
import { faClock, faTrash, faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import styled, { css } from 'styled-components/macro';
import { UnverifiedBankAccount } from '.';
import helperFunctions from '../../../../../../shareables/foundation/front-end/utils/helper-functions';
import APIButton, { APIButtonOptions } from '../../../../../../shareables/integrations/api/components/button';
import theme from '../../../../../../shareables/theme';
import { DeletePaymentMethodParameters } from '../../../../../../shareables/types/api/main/website/member/payment-method/delete';
import { GetPaymentMethodsResponse } from '../../../../../../shareables/types/api/main/website/member/payment-methods/get';
import { GeneratedThemeProps } from '../../../../../templates/template-style';


// Styled component
const TextLink = styled.span<{ theme: GeneratedThemeProps }>`
	color: ${(props) => props.theme.contentLinkColor};
	text-decoration: ${(props) => (props.theme.isPaper ? 'underline' : 'none')};
	cursor: pointer;
	
	&:hover,
	&:focus {
		color: ${(props) => props.theme.contentLinkHoverColor};
		
		${(props) =>
			props.theme.isPaper &&
			css`
				text-decoration: none;
			`};
	}
`;


// Define the accepted props
interface Props {
	paymentMethod: GetPaymentMethodsResponse['paymentMethods'][0];
	onChange: () => void;
	onVerify: (unverifiedBankAccount: UnverifiedBankAccount) => void;
	onLearnMore: (unverifiedBankAccount: UnverifiedBankAccount) => void;
}


// Function component
const StoredPaymentMethodRows: React.FC<Props> = ({ paymentMethod, onChange, onVerify, onLearnMore }) => {
	// Build button options
	const buttonOptions: APIButtonOptions<DeletePaymentMethodParameters, undefined, undefined> = {
		action: 'DELETE',
		uri: `/website/${window.location.hostname}/member/payment-method`,
		tooltip: 'Delete this payment method',
		warning: {
			message: 'Deleting this payment method is permanent.',
			button: `Yes, delete “${paymentMethod.brand} ending in ${paymentMethod.last4}”`,
		},
	};
	
	
	// Return JSX
	return (
		<tr>
			<td>
				{paymentMethod.brand} ending in {paymentMethod.last4}
				{paymentMethod.type === 'BANK_ACCOUNT' && paymentMethod.status === 'PENDING' && (
					<React.Fragment>
						<br />
						<small>
							<em>
								{paymentMethod.verification.recentlyAdded
									? `Awaiting microdeposit${paymentMethod.verification.microdeposit === 'amounts' ? 's' : ''}…`
									: 'Pending verification…'}
								<TextLink {...helperFunctions.buttonize(() => onLearnMore(paymentMethod))}> learn more</TextLink>
								{!paymentMethod.verification.recentlyAdded && (
									<React.Fragment>
										{' '}
										or <TextLink {...helperFunctions.buttonize(() => onVerify(paymentMethod))}>verify</TextLink>
									</React.Fragment>
								)}
							</em>
						</small>
					</React.Fragment>
				)}
			</td>
			
			<td>{paymentMethod.type === 'CARD' ? `${paymentMethod.expMonth}/${paymentMethod.expYear}` : 'N/A'}</td>
			
			<td style={{ textAlign: 'center' }}>
				{paymentMethod.type === 'CARD' || paymentMethod.status === 'ACTIVE' ? (
					<FontAwesomeIcon icon={faCheckCircle} color={theme.colors.success} />
				) : paymentMethod.type === 'BANK_ACCOUNT' && paymentMethod.status === 'PENDING' ? (
					<FontAwesomeIcon icon={faClock} />
				) : (
					<FontAwesomeIcon icon={faExclamationCircle} color={theme.colors.danger} />
				)}
			</td>
			
			<td style={{ textAlign: 'center' }}>
				<APIButton
					options={buttonOptions}
					color='danger'
					aria-label={`Delete “${paymentMethod.brand} ending in ${paymentMethod.last4}”`}
					onCompletion={onChange}
					smaller
					parameters={{
						id: paymentMethod.id,
					}}
				>
					<FontAwesomeIcon icon={faTrash} />
				</APIButton>
			</td>
		</tr>
	);
};

export default StoredPaymentMethodRows;
